import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SessionTimeoutManager } from "@/components/SessionTimeoutManager";
import SlackNudge from "@/components/SlackNudge";
import MenuGate from "@/components/MenuGate";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MenuVisibilityProvider } from "@/context/MenuVisibilityProvider";
import { ReadingLevelProvider } from "@/context/ReadingLevelContext";
import Landing from "@/pages/Landing";
import { Login, Signup } from "@/pages/Auth";
import { ForgotPassword, ResetPassword } from "@/pages/ResetPasswordFlow";
import AuthCallback from "@/pages/AuthCallback";
import AccessCodeLogin from "@/pages/AccessCodeLogin";
import Dashboard from "@/pages/Dashboard";
import Course from "@/pages/Course";
import { Modules, Downloads } from "@/pages/Member";
import Planner from "@/pages/Planner";
import DatabasesIndex from "@/pages/DatabasesIndex";
import DatabaseDetail from "@/pages/DatabaseDetail";
import ModuleDetail from "@/pages/ModuleDetail";
import DesignTokensPage from "@/pages/DesignTokens";
import Progress from "@/pages/Progress";
import LaunchBlueprint from "@/pages/LaunchBlueprint";
import FinalExam from "@/pages/FinalExam";
import Projects from "@/pages/Projects";
import SavedListView from "@/pages/SavedListView";
import FormulationProjectView from "@/pages/FormulationProjectView";
import Automation from "@/pages/Automation";
import FAQ from "@/pages/FAQ";
import { ToolsIndex, ToolEmbed } from "@/pages/Tools";
import Account from "@/pages/Account";
import AccountNotifications from "@/pages/AccountNotifications";
import AccountSubscription from "@/pages/AccountSubscription";
import CommunityProfile from "@/pages/CommunityProfile";
import BuyerAlerts from "@/pages/BuyerAlerts";
import StoryWall from "@/pages/StoryWall";
import VendorApply from "@/pages/VendorApply";
import PublicStory from "@/pages/PublicStory";
import DealsFeed from "@/pages/DealsFeed";
import Redeem from "@/pages/Redeem";
import StudentApply from "@/pages/StudentApply";
import Assistant from "@/pages/Assistant";
import AICenter from "@/pages/AICenter";
import AILabDashboard from "@/pages/AILab";
import AILabBrandProfile from "@/pages/AILab/BrandProfile";
import PackagingAILab from "@/pages/AILab/PackagingAILab";
import { AILabRoutes } from "@/pages/ai-lab/routes";
// CPG Agreements Phase 3 UI foundation — separate route tree, isolated
// import so it never touches /ai-lab or /ai-cpg-tools code paths.
import { AgreementsRoutes } from "@/pages/agreements/routes";
import AdminAgreementsAuthoring from "@/pages/AdminAgreementsAuthoring";
import AIStudioPackaging from "@/pages/AIStudioPackaging";
import AIStudioLogo from "@/pages/AIStudioLogo";
import AIStudioMetaAds from "@/pages/AIStudioMetaAds";
import AIStudioRedditAds from "@/pages/AIStudioRedditAds";
import AIStudioAdsDesigner from "@/pages/AIStudioAdsDesigner";
import AIStudioFormulation from "@/pages/AIStudioFormulation";
import AIToolPlaceholder from "@/pages/AIToolPlaceholder";
import Assessment from "@/pages/Assessment";
import Welcome from "@/pages/Welcome";
import Walkthrough from "@/pages/Walkthrough";
import Members from "@/pages/Members";
import IndustryDirectory from "@/pages/IndustryDirectory";
import IndustryVendorDetail from "@/pages/IndustryVendorDetail";
import { BillingSuccess } from "@/pages/Billing";
import Admin from "@/pages/Admin";import TeamAccept from "@/pages/TeamAccept";
import VerifyCredential from "@/pages/VerifyCredential";
import WorkbookList from "@/pages/WorkbookList";
import WorkbookWorksheet from "@/pages/WorkbookWorksheet";
import WorkbookDecisions from "@/pages/WorkbookDecisions";
import AdminCopyConsole from "@/pages/AdminCopyConsole";
import AdminMenuDirectory from "@/pages/AdminMenuDirectory";
import AdminPlanBuilder from "@/pages/AdminPlanBuilder";
import AdminPromptStudio from "@/pages/AdminPromptStudio";
import AdminMorganStudio from "@/pages/AdminMorganStudio";
import AdminKbDomains from "@/pages/AdminKbDomains";
import AdminLearnerProgress from "@/pages/AdminLearnerProgress";
import AdminLearnerProgressDetail from "@/pages/AdminLearnerProgressDetail";
import AdminDataExport from "@/pages/AdminDataExport";
import AccountKnowledgePage from "@/pages/AccountKnowledgePage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminAccountAssistantPage from "@/pages/AdminAccountAssistantPage";
import AdminAccountAssistantReviewPage from "@/pages/AdminAccountAssistantReviewPage";

function Protected({ children }) {
  const { status } = useAuth();
  const loc = useLocation();
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 rounded-full grad-band animate-pulse" />
      </div>
    );
  }
  if (status !== "authenticated") return <Navigate to={`/?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return children;
}

// Smart root — the Login UI is the app's home page. Signed-in visitors
// skip past it to /dashboard. Signed-out (or still-checking) visitors see
// the Login form immediately, with no client redirect for SEO cleanliness.
function SmartRoot() {
  const { status } = useAuth();
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 rounded-full grad-band animate-pulse" />
      </div>
    );
  }
  // `reason=timeout` = the session timeout is signing this tab out; show
  // the login form instead of bouncing back into the app.
  const signingOut = new URLSearchParams(window.location.search).get("reason") === "timeout";
  if (status === "authenticated" && !signingOut) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<SmartRoot />} />
      {/* Pricing page is hidden — every legacy pricing link, bookmark,
          or external redirect now lands on the authenticated Subscription
          & Billing page (login redirect handled by AppShell if the user
          isn't signed in yet). */}
      <Route path="/pricing" element={<Navigate to="/account/subscription" replace />} />
      {/* Legacy path — any old bookmark or external link that points at
          /login still lands on the canonical root. Kept as a client-side
          redirect (not a duplicate render) so crawlers see one URL. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/access-code-login" element={<AccessCodeLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/assessment" element={<Assessment />} />
      <Route path="/team/accept" element={<TeamAccept />} />
      <Route path="/verify/:credentialId" element={<VerifyCredential />} />
      <Route path="/vendors/apply" element={<VendorApply />} />
      <Route path="/stories/:id" element={<PublicStory />} />
      <Route path="/redeem" element={<Redeem />} />
      <Route path="/apply/student" element={<StudentApply />} />

      <Route path="/dashboard" element={<Protected><MenuGate itemId="nav-dashboard"><Dashboard /></MenuGate></Protected>} />
      <Route path="/course" element={<Protected><MenuGate itemId="nav-course"><Course /></MenuGate></Protected>} />
      <Route path="/modules" element={<Protected><MenuGate itemId="nav-modules"><Modules /></MenuGate></Protected>} />
      <Route path="/modules/:slug" element={<Protected><MenuGate itemId="nav-modules"><ModuleDetail /></MenuGate></Protected>} />
      <Route path="/design/tokens" element={<Protected><DesignTokensPage /></Protected>} />
      <Route path="/progress" element={<Protected><MenuGate itemId="nav-progress"><Progress /></MenuGate></Protected>} />
      <Route path="/blueprint" element={<Protected><MenuGate itemId="nav-blueprint"><LaunchBlueprint /></MenuGate></Protected>} />
      <Route path="/exam/final" element={<Protected><MenuGate itemId="nav-final-exam"><FinalExam /></MenuGate></Protected>} />
      <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
      <Route path="/ai-center" element={<Protected><MenuGate itemId="nav-ai-center"><AICenter /></MenuGate></Protected>} />
      {/* CPG AI Lab — new modular surface. `/ai-center` (above) still
          serves the legacy AI-Studio home so old bookmarks/emails work.
          Every /ai-lab surface is gated on `nav-ai-center` — if an
          admin hides that sidebar entry, direct URL hits redirect to
          /welcome instead of loading a page the founder can't reach
          from the nav. */}
      <Route path="/ai-lab" element={<Protected><MenuGate itemId="nav-ai-center"><AILabDashboard /></MenuGate></Protected>} />
      <Route path="/ai-lab/brand-profile" element={<Protected><MenuGate itemId="nav-ai-center"><AILabBrandProfile /></MenuGate></Protected>} />
      <Route path="/ai-lab/packaging" element={<Protected><MenuGate itemId="nav-ai-center"><PackagingAILab /></MenuGate></Protected>} />
      <Route path="/ai/packaging-designer" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioPackaging /></MenuGate></Protected>} />
      <Route path="/ai/logo-designer" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioLogo /></MenuGate></Protected>} />
      <Route path="/ai/meta-ads-manager" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioMetaAds /></MenuGate></Protected>} />
      <Route path="/ai/reddit-ads-manager" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioRedditAds /></MenuGate></Protected>} />
      <Route path="/ai/ads-designer" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioAdsDesigner /></MenuGate></Protected>} />
      <Route path="/ai/formulation-creator" element={<Protected><MenuGate itemId="nav-ai-center"><AIStudioFormulation /></MenuGate></Protected>} />
      {/* AI CPG Tools alias for the Formulation Creator — same page,
          new namespace. The old /ai/formulation-creator route above is
          preserved so existing bookmarks and legacy tiles keep working.
          This alias is gated on the AI-CPG-Tools sidebar entry
          (`nav-ai-cpg-tools`), matching the rest of /ai-cpg-tools/*. */}
      <Route path="/ai-cpg-tools/formulation-creator" element={<Protected><MenuGate itemId="nav-ai-cpg-tools"><AIStudioFormulation /></MenuGate></Protected>} />
      <Route path="/ai/:tool" element={<Protected><MenuGate itemId="nav-ai-center"><AIToolPlaceholder /></MenuGate></Protected>} />
      <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
      {/* /walkthrough is intentionally NOT wrapped in MenuGate: the
          Profile-menu row "Help & Walkthrough" links to it and is
          governed by its own Menu Directory id (`account-menu-help`).
          Gating this route on the sidebar `nav-walkthrough` would
          break that dropdown item when admins hide the sidebar link
          but keep the dropdown row enabled. */}
      <Route path="/walkthrough" element={<Protected><Walkthrough /></Protected>} />
      <Route path="/members" element={<Protected><MenuGate itemId="nav-members"><Members /></MenuGate></Protected>} />
      <Route path="/tools" element={<Protected><MenuGate itemId="nav-tools"><ToolsIndex /></MenuGate></Protected>} />
      <Route path="/tools/:slug" element={<Protected><MenuGate itemId="nav-tools"><ToolEmbed /></MenuGate></Protected>} />
      {/* Marketing Planner — visibility is now managed centrally via
          Admin Console → Menu Directory (item id: `nav-planner`). */}
      <Route path="/planner" element={<Protected><MenuGate itemId="nav-planner"><Planner /></MenuGate></Protected>} />
      <Route path="/downloads" element={<Protected><Downloads /></Protected>} />
      <Route path="/projects" element={<Protected><MenuGate itemId="nav-projects"><Projects /></MenuGate></Protected>} />
      <Route path="/projects/lists/:id" element={<Protected><MenuGate itemId="nav-projects"><SavedListView /></MenuGate></Protected>} />
      <Route path="/projects/formulations/:id" element={<Protected><MenuGate itemId="nav-projects"><FormulationProjectView /></MenuGate></Protected>} />
      <Route path="/automation" element={<Protected><Automation /></Protected>} />
      <Route path="/faq" element={<Protected><FAQ /></Protected>} />
      <Route path="/databases" element={<Protected><MenuGate itemId="nav-databases"><DatabasesIndex /></MenuGate></Protected>} />
      <Route path="/databases/:slug" element={<Protected><MenuGate itemId="nav-databases"><DatabaseDetail /></MenuGate></Protected>} />
      <Route path="/industry-directory" element={<Protected><MenuGate itemId="nav-industry-directory"><IndustryDirectory /></MenuGate></Protected>} />
      <Route path="/industry-directory/:slug" element={<Protected><MenuGate itemId="nav-industry-directory"><IndustryVendorDetail /></MenuGate></Protected>} />
      <Route path="/deals" element={<Protected><MenuGate itemId="nav-deals"><DealsFeed /></MenuGate></Protected>} />
      {/* /account and its subpages are NOT wrapped in MenuGate. They
          are reachable from the top-right Profile Menu whose rows
          have their own Menu Directory ids (`account-menu-profile`,
          `account-menu-notifications`, `account-menu-subscription`).
          Gating these routes on the sidebar `nav-account` would break
          the Profile dropdown whenever admins hide the sidebar link
          but keep the dropdown items enabled. AccountMenu.jsx already
          filters dropdown rows against the Menu Directory. */}
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/account/notifications" element={<Protected><AccountNotifications /></Protected>} />
      <Route path="/account/subscription" element={<Protected><AccountSubscription /></Protected>} />
      <Route path="/community-profile" element={<Protected><MenuGate itemId="nav-community-profile"><CommunityProfile /></MenuGate></Protected>} />
      <Route path="/buyer-alerts" element={<Protected><MenuGate itemId="nav-buyer-alerts"><BuyerAlerts /></MenuGate></Protected>} />
      <Route path="/story-wall" element={<Protected><MenuGate itemId="nav-story-wall"><StoryWall /></MenuGate></Protected>} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/copy-console" element={<Protected><AdminCopyConsole /></Protected>} />
      <Route path="/admin/menu-directory" element={<Protected><AdminMenuDirectory /></Protected>} />
      <Route path="/admin/plan-builder/:slug" element={<Protected><AdminPlanBuilder /></Protected>} />
      <Route path="/admin/prompt-studio" element={<Protected><AdminPromptStudio /></Protected>} />
      <Route path="/admin/morgan-studio" element={<Protected><AdminMorganStudio /></Protected>} />
      <Route path="/admin/kb-domains" element={<Protected><AdminKbDomains /></Protected>} />
      <Route path="/admin/progress" element={<Protected><AdminLearnerProgress /></Protected>} />
      <Route path="/admin/progress/:userId" element={<Protected><AdminLearnerProgressDetail /></Protected>} />
      <Route path="/admin/data-export" element={<Protected><AdminDataExport /></Protected>} />
      <Route path="/account/knowledge" element={<Protected><AccountKnowledgePage /></Protected>} />
      <Route path="/admin/feedback" element={<Protected><AdminFeedbackPage /></Protected>} />
      <Route path="/admin/account-assistant" element={<Protected><AdminAccountAssistantPage /></Protected>} />
      <Route path="/admin/account-assistant-review" element={<Protected><AdminAccountAssistantReviewPage /></Protected>} />
      <Route path="/workbook" element={<Protected><MenuGate itemId="nav-workbook"><WorkbookList /></MenuGate></Protected>} />
      <Route path="/workbook/:worksheetId" element={<Protected><MenuGate itemId="nav-workbook"><WorkbookWorksheet /></MenuGate></Protected>} />
      <Route path="/decisions" element={<Protected><MenuGate itemId="nav-decisions"><WorkbookDecisions /></MenuGate></Protected>} />

      {/* CPG AI CPG Tools (Phase 3 UI foundation). Mounted after all existing
          routes; guarded by the same `Protected` wrapper used above. */}
      {AILabRoutes({ guard: Protected })}

      {/* CPG Agreements Phase 3 UI foundation. Additive mount after every
          existing route so the mount order + numbering above stays exactly
          as it was. Guarded by the same `Protected` wrapper so /agreements
          requires login. */}
      {AgreementsRoutes({ guard: Protected })}
      <Route path="/admin/agreements-authoring" element={<Protected><AdminAgreementsAuthoring /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ReadingLevelProvider>
      <BrowserRouter>
        <MenuVisibilityProvider>
          <Toaster position="top-right" closeButton />
          <SlackNudge />
          <SessionTimeoutManager />
          <AppRouter />
        </MenuVisibilityProvider>
      </BrowserRouter>
      </ReadingLevelProvider>
    </AuthProvider>
  );
}
